import type { NavigationArea, NavigationItem, NavigationSection, NavigationSectionKey } from './config';
import { appRoutes } from '@routes/appRoutes';

type LicenseMode = 'CONFINEMENT_ONLY' | 'PASTURE_ONLY' | 'BOTH';
type LicenseFeature = 'automation';

type NavigationUser = {
  isAdmin?: boolean;
  role?: 'leader' | 'normal';
  accessLevel?: 'admin' | 'leader' | 'normal';
  view?: string[];
  create?: string[];
  upgrade?: string[];
  del?: string[];
};

export interface NavigationFilterContext {
  isAuthenticated: boolean;
  user?: NavigationUser;
  farm?: string;
  licenseMode?: LicenseMode;
  licenseFeatures?: LicenseFeature[];
}

interface NavigationAccessOptions {
  /**
   * O guard de rota (`RouteAccessGuard`) delega a exigência de fazenda ao
   * `ConfirInfosError` (que mostra o seletor de fazenda) — aqui só interessam
   * permissão/nível/licença. O menu continua usando o gate completo.
   */
  ignoreFarmRequirement?: boolean;
}

export function canAccessNavigationItem(
  item: NavigationItem,
  context: NavigationFilterContext,
  options?: NavigationAccessOptions,
) {
  const { isAuthenticated, user, farm, licenseMode, licenseFeatures } = context;

  if (item.farmRequired && !farm && !options?.ignoreFarmRequirement) {
    return false;
  }

  if (item.licenseScope === 'CONFINEMENT' && licenseMode === 'PASTURE_ONLY') {
    return false;
  }

  if (item.licenseScope === 'PASTURE' && licenseMode === 'CONFINEMENT_ONLY') {
    return false;
  }

  // Feature adicional: ausente por padrão, então o item só aparece quando a
  // licença da instalação declara a feature explicitamente.
  if (
    item.licenseFeature &&
    !(licenseFeatures || []).includes(item.licenseFeature)
  ) {
    return false;
  }

  // Itens restritos por nivel de acesso (ex.: Licencas admin-only) nao possuem
  // module, entao o gate de accessLevel precisa rodar antes do atalho abaixo.
  if (item.accessLevels?.length) {
    if (!isAuthenticated || !user) {
      return false;
    }
    const accessLevel =
      user.accessLevel || (user.isAdmin ? 'admin' : user.role || 'normal');
    if (!item.accessLevels.includes(accessLevel)) {
      return false;
    }
  }

  if (!item.module) {
    return true;
  }

  if (!isAuthenticated || !user) {
    return false;
  }

  if (!item.role) {
    return true;
  }

  const permissions = user[item.role];
  if (!permissions) {
    return false;
  }

  return permissions.includes(item.module);
}

export function getVisibleNavigationItems(
  items: NavigationItem[],
  context: NavigationFilterContext,
) {
  return items.filter((item) => canAccessNavigationItem(item, context));
}

export function groupNavigationItems(
  items: NavigationItem[],
  sections: NavigationSection[],
) {
  const grouped = new Map<NavigationSectionKey, NavigationItem[]>();

  items.forEach((item) => {
    const current = grouped.get(item.section) || [];
    current.push(item);
    grouped.set(item.section, current);
  });

  return sections
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((section) => ({
      section,
      items: (grouped.get(section.key) || [])
        .slice()
        .sort((a, b) => a.order - b.order),
    }))
    .filter((group) => group.items.length > 0);
}

/** Área da navegação a que a rota pertence: o Financeiro tem barra lateral própria. */
export function getNavigationArea(pathname: string): NavigationArea {
  const root = appRoutes.financeiro.root;
  return pathname === root || pathname.startsWith(`${root}/`) ? 'financial' : 'management';
}

export function getSectionsForArea(sections: NavigationSection[], area: NavigationArea) {
  return sections.filter((section) => section.area === area);
}

/** Rota do primeiro item visível da área, na ordem das seções e itens; null = nenhum. */
export function getFirstAreaRoute(
  visibleItems: NavigationItem[],
  sections: NavigationSection[],
  area: NavigationArea,
): string | null {
  const groups = groupNavigationItems(visibleItems, getSectionsForArea(sections, area));
  return groups.at(0)?.items.at(0)?.route ?? null;
}

export function isNavigationItemActive(item: NavigationItem, pathname: string) {
  const prefixes =
    item.activePrefixes && item.activePrefixes.length > 0
      ? item.activePrefixes
      : [item.route];

  return prefixes.some((prefix) => {
    if (!prefix || prefix === '/') {
      return pathname === prefix;
    }

    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}

function normalizeNavigationSearch(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function searchNavigationItems(items: NavigationItem[], rawQuery: string) {
  const query = normalizeNavigationSearch(rawQuery);

  if (!query) {
    return [];
  }

  const queryTokens = query.split(/\s+/).filter(Boolean);

  return items
    .map((item) => {
      const label = normalizeNavigationSearch(item.label);
      const route = normalizeNavigationSearch(item.route);
      const keywords = item.searchKeywords.map(normalizeNavigationSearch);
      const haystacks = [label, route, ...keywords];

      const matchesAllTokens = queryTokens.every((token) =>
        haystacks.some((entry) => entry.includes(token)),
      );

      if (!matchesAllTokens) {
        return null;
      }

      let score = 0;

      if (label === query) score += 1000;
      if (label.startsWith(query)) score += 300;
      if (label.includes(query)) score += 150;
      if (route.includes(query)) score += 80;

      keywords.forEach((keyword) => {
        if (keyword === query) score += 120;
        else if (keyword.startsWith(query)) score += 70;
        else if (keyword.includes(query)) score += 35;
      });

      queryTokens.forEach((token) => {
        if (label.startsWith(token)) score += 40;
        if (label.includes(token)) score += 20;
      });

      score -= item.order;

      return { item, score };
    })
    .filter((entry): entry is { item: NavigationItem; score: number } => Boolean(entry))
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      if (left.item.section !== right.item.section) {
        return left.item.section.localeCompare(right.item.section);
      }

      return left.item.order - right.item.order;
    })
    .map((entry) => entry.item);
}
